// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { useGetIdentity, useGetList } from 'react-admin'
import { petAssigneeIds } from '../clinics/clinicAssociation'
import type { Pet } from '../types'

/**
 * Display names for a pet's assignees (vic/vetpo#70).
 *
 * The pet record carries `assignedVetIds` (bare ids) and, at depth >= 1, only
 * the FIRST assignee populated. Names for the rest come from what the caller
 * can already read without a new endpoint: itself (identity) and — for a
 * master — its sub-accounts (one cached `users` list shared by every row, so a
 * 50-row Pets list is still one request). An id that resolves nowhere (another
 * clinic's vet on a shared patient) renders as `#id`, never as blank.
 */
export const useAssigneeNames = (pet: Pet | any): string[] => {
  const { identity } = useGetIdentity()
  const isMaster =
    (identity?.role === 'vet' || identity?.role === 'vetadmin') && !identity?.masterAccount
  const { data: subs = [] } = useGetList(
    'users',
    {
      filter: { masterAccount: identity?.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: isMaster && !!identity?.id },
  )

  const names = new Map<number, string>()
  for (const u of subs as any[]) names.set(Number(u.id), u.name || u.email)
  if (identity?.id != null) {
    names.set(Number(identity.id), (identity as any).fullName || (identity as any).name || '')
  }
  const legacy = pet?.assignedVet
  if (legacy && typeof legacy === 'object' && legacy.id != null) {
    names.set(Number(legacy.id), legacy.name || legacy.email || '')
  }
  return petAssigneeIds(pet).map((id) => names.get(id) || `#${id}`)
}
